// src/components/molecules/VideoPlayer.js

import React, { useRef, useState, useImperativeHandle } from "react";
import { Box } from "@mui/material";
import VideoElement from "../atoms/VideoElement";
import VideoPlayButton from "../atoms/VideoPlayButton";
import VideoVolumeControl from "../atoms/VideoVolumeControl";
import VideoFullscreenButton from "../atoms/VideoFullscreenButton";
import PropTypes from "prop-types";

const VideoPlayer = React.forwardRef(
  ({ src, onTimeUpdate, onLoadedMetadata }, ref) => {
    const videoRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [volume, setVolume] = useState(1);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const handlePlayPause = () => {
      const video = videoRef.current;
      if (!video) return;

      if (isPlaying) {
        video.pause();
      } else {
        video.play();
      }
      setIsPlaying(!isPlaying);
    };

    const handleVolumeChange = (event, newValue) => {
      setVolume(newValue);
      if (videoRef.current) {
        videoRef.current.volume = newValue;
      }
    };

    const handleFullscreen = () => {
      const videoContainer = videoRef.current.parentElement;
      if (!document.fullscreenElement) {
        videoContainer.requestFullscreen();
        setIsFullscreen(true);
      } else {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    };

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
      seekTo: (time) => {
        if (videoRef.current) {
          videoRef.current.currentTime = time;
        }
      },
      getCurrentTime: () => {
        return videoRef.current ? videoRef.current.currentTime : 0;
      },
      getDuration: () => {
        return videoRef.current ? videoRef.current.duration : 0;
      },
      pause: () => {
        if (videoRef.current) videoRef.current.pause();
        setIsPlaying(false);
      },
      play: () => {
        if (videoRef.current) videoRef.current.play();
        setIsPlaying(true);
      },
    }));

    return (
      <Box position="relative" width="100%">
        <VideoElement
          ref={videoRef}
          src={src}
          onTimeUpdate={onTimeUpdate}
          onLoadedMetadata={onLoadedMetadata}
        />
        {/* Controls Overlay */}
        <Box
          position="absolute"
          bottom={0}
          left={0}
          right={0}
          bgcolor="rgba(0, 0, 0, 0.6)"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          p={1}
        >
          <VideoPlayButton isPlaying={isPlaying} onClick={handlePlayPause} />
          <VideoVolumeControl
            volume={volume}
            onVolumeChange={handleVolumeChange}
          />
          <VideoFullscreenButton
            isFullscreen={isFullscreen}
            onClick={handleFullscreen}
          />
        </Box>
      </Box>
    );
  }
);

VideoPlayer.propTypes = {
  src: PropTypes.string.isRequired,
  onTimeUpdate: PropTypes.func.isRequired,
  onLoadedMetadata: PropTypes.func.isRequired,
};

export default VideoPlayer;
